/*
  shintop-contact-topics.js — the explainer cards become a route into the form.

  Round 25, 2026-09-24, from the reference mock Naoya supplied. Each explainer card ended in a
  paragraph and stopped; the trade one sits 325px BELOW the Send button, so a business enquiry
  could be sent before the page said what to put in it. Now each card ends in a button that picks
  the matching topic chip and moves the visitor to the form with it already chosen.

  PROGRESSIVE ENHANCEMENT, AND THE `hidden` ATTRIBUTE IS THE WHOLE MECHANISM. The buttons are
  rendered `hidden` by the section and un-hidden here, so a visitor without JavaScript never sees
  a control that could not work. §4 rule 37: `hidden` is the browser's and any `display` in a
  stylesheet takes it away — nothing in `component-shintop-contact.css` gives these a display.

  ⚠️ The no-JS state is the one a probe cannot see by the time it runs (2026-09-12, guides index):
  by then the element has already run. To measure it, set `el.hidden = true` in the eval and read
  `getComputedStyle().display` — do not trust the attribute alone.

  IT MATCHES ON THE TOPIC'S TEXT, not on an index. The topics are one comma-separated setting and
  the card's topic is another; an index would silently point at the wrong chip the first time a
  topic is inserted rather than appended. A button whose topic matches nothing does not appear at
  all, which is the honest answer to a mismatch and is checked before the button is shown.

  It never submits, never navigates and touches no value but the topic the visitor asked for.
*/
class ShintopContactTopics extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById('ShintopContactForm');
    if (!this.form) return;

    this.radios = Array.from(this.form.querySelectorAll('.shintop-contact__chip input[type="radio"]'));
    if (!this.radios.length) return;

    Array.from(document.querySelectorAll('.shintop-contact__card-cta')).forEach((button) => {
      const wanted = button.getAttribute('data-shintop-topic');
      const radio = this.radios.find((r) => r.value === wanted);
      /* No matching chip: leave it hidden. A button that would select nothing is worse than no
         button, and this is the only place the two settings can be proven to agree. */
      if (!radio) return;
      button.hidden = false;
      button.addEventListener('click', () => this.choose(radio));
    });
  }

  choose(radio) {
    radio.checked = true;
    /* Fire it so anything else listening — the stash below, a future hint — sees a real change
       rather than a silent property write. A synthetic event tests our listeners, not the
       browser's, which is fine here because ours are the only ones that need to run. */
    radio.dispatchEvent(new Event('change', { bubbles: true }));

    const fieldset = radio.closest('.shintop-contact__topics') || this.form;
    fieldset.scrollIntoView({ block: 'center' });
    /* Focus after the scroll so the browser does not scroll a second time to its own idea of
       where the control is. `preventScroll` keeps the two from fighting. */
    radio.focus({ preventScroll: true });
  }
}

if (!customElements.get('shintop-contact-topics')) {
  customElements.define('shintop-contact-topics', ShintopContactTopics);
}
